/** @jsxImportSource @emotion/react */

import { Pencil, Plus, SquareArrowOutUpRight } from 'lucide-react';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';
import { MANUAL } from 'shared/constants/links';
import { useFeature } from 'shared/hooks/use-feature';
import { history } from 'store/store';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import EmptyButton from 'ud-ui/components/empty-button';
import UDTippy from 'ud-ui/components/tippy';
import backgroundElems from 'modules/overlay-canvas/addons/background-elems';
import useFabric from 'modules/overlay-canvas/hooks/use-fabric';
import BoardItem from 'modules/template-editor/ui/components/view-boards/boards-list/board-item';
import BoardsList from 'modules/template-editor/ui/components/view-boards/boards-list/boards-list';
import UdHeaderViewBoards from 'modules/template-editor/ui/components/view-boards/right/ud-header-view-boards';
import { CanvasWrapper, Container, LeftContainer, RightContainer } from 'modules/template-editor/ui/components/view-boards/styles';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { setHasChanges, setSavedScheme } from 'modules/template-editor/store/editor/editor';
import { setActiveBoard } from 'modules/template-editor/store/templates/templates';
import LeftMenuSkeleton from '../components/skeletons/left-menu-skeleton';
import useCreateModal from '../hooks/modals/board/create';
import useBoards from '../hooks/use-boards';
import { useLoadTemplates } from '../hooks/use-load-templates';
import useLocalStorage from '../hooks/use-local-storage';
import useTemplates from '../hooks/use-templates';
import { MainBlock } from './styles';
import { theme } from 'styles/theme/theme';

function Boards() {
  const { t } = useLingui();
  const dispatch = useDispatch();
  const [isInit, setInit] = useState<boolean>(false);
  const { activeTemplate } = useTemplates();
  const { boards, activeBoard, activeScheme, isLoading } = useBoards();
  const { canvas, manager } = useFabric('static', {
    data: { ...activeTemplate?.resolution },
    style: { borderRadius: 8, position: 'absolute' },
    resizing: true,
  });
  const { setHeaderContent } = useMainLayout();

  useLoadTemplates();

  const { clearData } = useLocalStorage();

  useEffect(() => {
    clearData();
    dispatch(setSavedScheme(undefined));
    dispatch(setHasChanges(false));
  }, [clearData, dispatch]);

  useEffect(() => {
    if (!boards.length) {
      return;
    }
    if (!activeBoard || !activeTemplate || !boards.find((board) => board.id === activeBoard.id)) {
      dispatch(setActiveBoard({ id: boards[0].id }));
    }
  }, [activeTemplate, activeBoard, boards, dispatch]);

  // Менять табло трансляции (создавать, править, дублировать, удалять) можно только по подписке с OBS_INTEGRATION.
  const { isEnabled: hasObsIntegration } = useFeature('OBS_INTEGRATION');

  const isEditable = useMemo(() => {
    if (!activeTemplate || !hasObsIntegration) {
      return false;
    }
    return activeTemplate.access !== 'default';
  }, [activeTemplate, hasObsIntegration]);

  useEffect(() => {
    if (!manager) {
      return;
    }
    backgroundElems.init(manager.canvas);
    setInit(true);
  }, [manager]);

  useEffect(() => {
    if (!activeTemplate) {
      return;
    }
    manager?.setResolution(activeTemplate?.resolution);
    manager?.normalizedViewport();
  }, [activeTemplate, manager]);

  useEffect(() => {
    if (!manager) {
      return;
    }
    if (!activeScheme) {
      return;
    }
    manager.loadLocal({ scheme: activeScheme, safeBackground: true }).then(() => {
      manager.normalizedViewport();
    });
  }, [activeBoard, activeScheme, manager]);

  const onGoEdit = useCallback(() => {
    if (!isInit) {
      return;
    }
    if (!activeTemplate) {
      return;
    }
    if (!activeBoard) {
      return;
    }
    if (!isEditable) {
      return;
    }

    history.push(Pages.TEMPLATE.builders.editor(activeTemplate.id, activeBoard.id), {
      from: 'boards',
    });
  }, [activeBoard, activeTemplate, isEditable, isInit]);

  const breadcrumb = useMemo(() => {
    return (
      <UDBreadcrumbs
        className="my-10"
        breadcrumbs={[
          { label: t`Трансляции`, to: Pages.TEMPLATE.INDEX },
          { label: activeTemplate?.name ?? t`Табло`, to: Pages.TEMPLATE.builders.boards(activeTemplate?.id) },
        ]}
      />
    );
  }, [activeTemplate?.id, activeTemplate?.name, t]);

  const { modal: createModal, open: openCreateModal } = useCreateModal();
  const redirectToManual = useCallback(() => {
    window.open(MANUAL, '_blank');
  }, []);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  return (
    <>
      <MainBlock>
        <Container style={{ opacity: isLoading ? 0.5 : 1 }}>
          {isLoading ? (
            <LeftMenuSkeleton showAddNewBoard={isEditable} />
          ) : (
            <LeftContainer>
              <BoardsList
                items={boards.map((board) => {
                  return (
                    <BoardItem
                      key={board.id}
                      board={board}
                      isEditable={hasObsIntegration && (isEditable || boards.length <= 1)}
                      onClick={() => dispatch(setActiveBoard({ id: board.id }))}
                      isActive={board.id === activeBoard?.id}
                    />
                  );
                })}
                headerText={t`Табло`}
              />
              {isEditable && (
                <EmptyButton disabled={isLoading} onClick={openCreateModal} color={theme.colors.Gray96} icon={<Plus size={16} />}>
                  <Trans>Добавить табло</Trans>
                </EmptyButton>
              )}
            </LeftContainer>
          )}

          <RightContainer>
            <UdHeaderViewBoards
              mainText={activeBoard?.name}
              buttons={[
                <UDTippy key={'edit'} content={hasObsIntegration ? undefined : t`Не доступно в вашей подписке`} placement={'top'}>
                  <span className="inline-flex">
                    <EmptyButton disabled={!isEditable} onClick={onGoEdit} icon={<Pencil size={20} />}>
                      <Trans>Редактировать</Trans>
                    </EmptyButton>
                  </span>
                </UDTippy>,
              ]}
              subElems={[
                <EmptyButton key={'manual'} onClick={redirectToManual} icon={<SquareArrowOutUpRight size={18} />}>
                  <Trans>Инструкция</Trans>
                </EmptyButton>,
              ]}
            />
            <CanvasWrapper>{canvas}</CanvasWrapper>
          </RightContainer>
        </Container>
      </MainBlock>
      {createModal}
    </>
  );
}

export default Boards;
